import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as EmailValidator from 'email-validator';
import {
  Radio,
  RadioGroup,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Chip,
  Typography,
  MenuItem,
} from '@material-ui/core';
import { Cancel } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import DateSelect from '../../../components/DateSelect';
import Button from '../../../components/Button';
import PatientSearchBar from '../../../components/PatientSearchBar';
import PhoneInput from '../../../components/PhoneInput';
import { isEmailAvailable } from '../../../services/api';
import { ADULTHOOD_AGE, STATES_LIST } from '../../../constants';
import { fetchCurrentUser } from '../../../services/api/auth';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { fetchTebraInsurancesList } from '../../../services/api/admin';

function generateString(length) {
  // program to generate random strings
  let characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = ' ';
  let charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
function PatientProfile(props) {
  const {
    patient,
    onSubmit,
    onClose,
    backButtonText,
    source='',
    classes: { input, button, warning },
  } = props;
  const initGuardians = (patient.guardians || []).reduce((acc, guardian) => {
    acc[guardian.id] = guardian.name;
    return acc;
  }, {});

  const { enqueueSnackbar } = useSnackbar();
  var date = new Date();
  let minYears = date.setFullYear(date.getFullYear() - ADULTHOOD_AGE);

  const [firstName, setFirstName] = useState(patient.firstName || '');
  const [lastName, setLastName] = useState(patient.lastName || '');
  const [preferredName, setPreferredName] = useState(patient.preferredName || '');
  // const [email, setEmail] = useState(patient.email && !patient.email.includes('@example.com') ? patient.email :'');
  const [email, setEmail] = useState(patient.email ? patient.email : '');
  const [phone, setPhone] = useState(patient.phone || '');
  const [gender, setGender] = useState(patient.gender || '');
  const [birthdate, setBirthdate] = useState(patient.birthdate);
  const [zipcode, setZipcode] = useState(patient.zipcode || '');
  const [tebraId, setTebraId] = useState(patient.tebraPatientId || '');
  const [validationErrors, setValidationErrors] = useState({});
  const [guardians, setGuardians] = useState(initGuardians);
  const [showGuardianSelection, setShowGuardianSelection] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // To get the current user's info.

  // These are insurances that has no group number.
  const insuranceWithoutGroupNumber = ['729d4056-09e9-4b3a-a37b-8a236bb154ab', '74a91228-6503-4851-922d-d4e23bc402ff', '1d1dae88-1d16-4e3b-a63d-a32e74a88e0a']

    useEffect(() => {
    async function loadUser() {
      const user = await fetchCurrentUser();
      setCurrentUser(user);
    }
    loadUser();
  }, []);
  const [insurancePlans, setInsurancePlans] = useState([]);
  const [addressLine1, setAddressLine1] = useState(patient.addressLine1 || '');
  const [addressLine2, setAddressLine2] = useState(patient.addressLine2 || '');
  const [city, setCity] = useState(patient.city || '');
  const [state, setState] = useState(patient.state || '');
  const [insurancePlanId, setInsurancePlanId] = useState(patient.insurancePlanId || null);
  const [insuranceId, setInsuranceId] = useState(patient.insuranceId || '');
  const [payerId, setPayerId] = useState(patient.payerId || '');
  const [groupNumber, setGroupNumber] = useState(patient.groupNumber || '');

  useEffect(() => {
    setShowGuardianSelection(
      isMinor(birthdate) || Object.keys(guardians).length > 0
    );
  }, [guardians]);

  useEffect(() => {
    (async () => {
      const response = await fetchTebraInsurancesList();
      setInsurancePlans(response.data);
    })();
  }, []);

  const isMinor = birthTime => {
    const now = new Date().getTime();
    const then = new Date(birthTime).getTime();
    return (now - then) / 1000 / 60 / 60 / 24 / 365 < ADULTHOOD_AGE;
  };

  const onChange = setter => event => {
    const value =
      event.target.name === 'state'
        ? event.target.value.toUpperCase().slice(0, 2)
        : event.target.value;
    setter(value);
    setValidationErrors({ ...validationErrors, [event.target.name]: false });
  };

  const validate = () => {
    const guardianIds = Object.keys(guardians);
    let valid = true;
    const nextState = {};
    if (!firstName.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.firstName = 'Required';
    }
    if (!lastName.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.lastName = 'Required';
    }
    if (guardianIds.length === 0) {
      if (!email) {
        valid = false;
        nextState.email = 'Required';
      } else if (!EmailValidator.validate(email)) {
        valid = false;
        nextState.email = 'Must be a valid email';
      }
      if (!phone) {
        valid = false;
        nextState.phone = 'Required';
      }
      if (!zipcode) {
        valid = false;
        nextState.zipcode = 'Required';
      } else if (!zipcode.match(/^\d{5}$|^\d{5}-\d{4}$/)) {
        valid = false;
        nextState.zipcode = 'Must be a valid zipcode';
      }
    }
    if (!addressLine1?.trim()) {
      valid = false;
      nextState.addressLine1 = 'Required';
    }
    if (!city?.trim()) {
      valid = false;
      nextState.city = 'Required';
    }
    if (!state?.trim()) {
      valid = false;
      nextState.state = 'Required';
    } else if (!state.match(/^[A-Z]{2}$/)) {
      valid = false;
      nextState.state = 'Must be a valid 2-letter state code';
    }
    if (!insurancePlanId) {
      valid = false;
      nextState.insurancePlanId = 'Required';
    }
    if (!insuranceId?.trim()) {
      valid = false;
      nextState.insuranceId = 'Required';
    }
    // For Medicare insurance group number is not required.
    if (!groupNumber?.trim() && !insuranceWithoutGroupNumber?.includes(insurancePlanId)) {
      valid = false;
      nextState.groupNumber = 'Required';
    }
    if (!gender) {
      valid = false;
      nextState.gender = 'Required';
    }
    if (!birthdate) {
      valid = false;
      nextState.birthdate = 'Required';
    }
    setValidationErrors(nextState);
    return valid;
  };

  return (
    <div>
      <TextField
        fullWidth
        label="First Name"
        name="firstName"
        value={firstName}
        variant="outlined"
        onChange={onChange(setFirstName)}
        error={validationErrors.firstName}
        helperText={validationErrors.firstName}
        className={input}
      />

      <TextField
        fullWidth
        label="Last Name"
        value={lastName}
        variant="outlined"
        name="lastName"
        onChange={onChange(setLastName)}
        error={validationErrors.lastName}
        helperText={validationErrors.lastName}
        className={input}
      />

      <TextField
        fullWidth
        label="Preferred Name"
        value={preferredName}
        variant="outlined"
        name="preferredName"
        onChange={onChange(setPreferredName)}
        error={validationErrors.preferredName}
        helperText={validationErrors.preferredName}
        className={input}
      />

      <TextField
        fullWidth
        label="Email"
        value={email}
        variant="outlined"
        name="email"
        onChange={onChange(setEmail)}
        error={validationErrors.email}
        helperText={validationErrors.email}
        inputProps={{
          onBlur: async () => {
            const available = await isEmailAvailable(email);
            if (!available) {
              setValidationErrors({
                ...validationErrors,
                email: 'this email is already in use',
              });
            }
          },
        }}
        className={input}
      />

      <TextField
        fullWidth
        label="Phone Number"
        value={phone}
        variant="outlined"
        name="phone"
        onChange={onChange(setPhone)}
        error={validationErrors.phone}
        helperText={validationErrors.phone}
        className={input}
        InputProps={{
          inputComponent: PhoneInput,
        }}
      />

      <TextField
        fullWidth
        label="Address Line 1"
        value={addressLine1}
        name="addressLine1"
        variant="outlined"
        onChange={onChange(setAddressLine1)}
        error={validationErrors.addressLine1}
        helperText={validationErrors.addressLine1}
        className={input}
      />

      <TextField
        fullWidth
        label="Address Line 2"
        value={addressLine2}
        name="addressLine2"
        variant="outlined"
        onChange={onChange(setAddressLine2)}
        error={validationErrors.addressLine2}
        helperText={validationErrors.addressLine2}
        className={input}
      />

      <TextField
        fullWidth
        label="City"
        value={city}
        name="city"
        variant="outlined"
        onChange={onChange(setCity)}
        error={!!validationErrors.city}
        helperText={validationErrors.city}
        className={input}
      />

      <Autocomplete
        options={STATES_LIST}
        getOptionLabel={option =>
          option ? `${option.code} - ${option.name}` : ''
        }
        value={STATES_LIST.find(option => option.code === state) || null}
        onChange={(_, value) => {
          setState(value?.code || '');
          setValidationErrors({
            ...validationErrors,
            state: false,
          });
        }}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            label="State"
            name="state"
            variant="outlined"
            error={!!validationErrors.state}
            helperText={validationErrors.state}
            className={input}
          />
        )}
      />

      <TextField
        fullWidth
        label="Zip Code"
        value={zipcode}
        name="zipcode"
        variant="outlined"
        onChange={onChange(setZipcode)}
        error={validationErrors.zipcode}
        helperText={validationErrors.zipcode}
        className={input}
      />

      {(currentUser?.admin && (source === 'patientProfilePage' || source === 'miniPatientProfile') ) && <TextField
        fullWidth
        label="Patient's Tebra ID"
        value={tebraId}
        name="tebraId"
        variant="outlined"
        onChange={onChange(setTebraId)}
        className={input}
        disabled={
          !(
            currentUser?.admin &&
            (source === 'patientProfilePage' || source === 'miniPatientProfile')
          )
        }
      />}

      <Autocomplete
        options={insurancePlans}
        getOptionLabel={option =>
          option ? `${option.companyName} - ${option.planName} (${option.payerId})` : ''
        }
        filterOptions={(options, { inputValue }) => {
          const searchValue = inputValue.toLowerCase();

          return options.filter(
            option =>
              option.companyName?.toLowerCase().includes(searchValue) ||
              option.planName?.toLowerCase().includes(searchValue) ||
              option.payerId?.toLowerCase().includes(searchValue)
          );
        }}
        value={
          insurancePlans.find(item => item.id === insurancePlanId) || null
        }
        onChange={(event, selectedPlan) => {
          setInsurancePlanId(selectedPlan?.id || null);
          setPayerId(selectedPlan?.payerId || '');

          setValidationErrors(prev => ({
            ...prev,
            insurancePlanId: false,
          }));
        }}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            label="Insurance Name / Payer ID"
            variant="outlined"
            error={!!validationErrors.insurancePlanId}
            helperText={validationErrors.insurancePlanId}
            className={input}
          />
        )}
      />

      <TextField
        fullWidth
        InputProps={{
          readOnly: true,
          disabled: true
        }}
        label="Payer ID"
        value={payerId}
        name="payerId"
        variant="outlined"
        onChange={onChange(setPayerId)}
        error={validationErrors.payerId}
        helperText={validationErrors.payerId}
        className={input}
      />

      <TextField
        fullWidth
        label="Insurance ID"
        value={insuranceId}
        name="insuranceId"
        variant="outlined"
        onChange={onChange(setInsuranceId)}
        error={validationErrors.insuranceId}
        helperText={validationErrors.insuranceId}
        className={input}
      />

    { !insuranceWithoutGroupNumber?.includes(insurancePlanId) && <TextField
      fullWidth
      label="Group Number"
      value={groupNumber}
      name="groupNumber"
      variant="outlined"
      onChange={onChange(setGroupNumber)}
      error={validationErrors.groupNumber}
      helperText={validationErrors.groupNumber}
      className={input}
    /> }

      <FormControl component="fieldset" variant="filled" className={input}>
        <FormLabel component="legend">
          Gender (as listed on health insurance card)
        </FormLabel>
        <RadioGroup
          row
          aria-label="Gender"
          name="gender"
          value={gender}
          onChange={onChange(setGender)}
        >
          <FormControlLabel value="female" control={<Radio />} label="Female" />
          <FormControlLabel value="male" control={<Radio />} label="Male" />
        </RadioGroup>
        {validationErrors.gender && (
          <FormLabel component="legend" className={warning}>
            {validationErrors.gender}
          </FormLabel>
        )}
      </FormControl>

      <DateSelect
        disableFuture
        fullWidth
        label="Birthdate"
        format="MM/DD/YYYY"
        maxDate={new Date(minYears)}
        placeholder="MM/DD/YYYY ex.10/02/1981"
        value={new Date(birthdate)}
        error={validationErrors.birthdate}
        helperText={validationErrors.birthdate}
        onChange={(date) => {
          setBirthdate(moment.utc(date).format('MM/DD/YYYY'));
          setShowGuardianSelection(isMinor(date));
          setValidationErrors({ ...validationErrors, birthdate: false });
        }}
      />

      {showGuardianSelection && (
        <>
          <PatientSearchBar
            label="Guardian"
            onSelect={(guardian) => {
              if (guardian) {
                setGuardians({
                  ...guardians,
                  [guardian.id]: `${guardian.firstName} ${guardian.lastName}`,
                });
              }
            }}
          />
          <div>
            {Object.keys(guardians).map((guardianId) => (
              <Chip
                key={guardianId}
                onDelete={() => {
                  delete guardians[guardianId];
                  setGuardians({ ...guardians });
                }}
                clickable
                deleteIcon={<Cancel />}
                label={guardians[guardianId]}
              />
            ))}
            {Object.keys(guardians).length === 0 && showGuardianSelection ? (
              <Typography className={warning}>Required</Typography>
            ) : (
              ''
            )}
          </div>
        </>
      )}

      <div>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          className={button}
        >
          {backButtonText || 'Back'}
        </Button>

        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            if (showGuardianSelection && Object.keys(guardians).length === 0) {
              enqueueSnackbar('Please select Guardian', {
                variant: 'warning',
                preventDuplicate: true,
                autoHideDuration: 3000,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
              });
            } else
              onSubmit({
                ...patient,
                firstName: firstName.replace(/^\s+|\s+$/gm, ''),
                lastName: lastName.replace(/^\s+|\s+$/gm, ''),
                preferredName: preferredName.replace(/^\s+|\s+$/gm, ''),
                email: email ? email : `${generateString(10)}@example.com`,
                phone,
                gender,
                birthdate: moment.utc(birthdate).format('MM/DD/YYYY'),
                zipcode,
                guardianIds: Object.keys(guardians),
                addressLine1,
                addressLine2,
                city,
                state,
                insurancePlanId,
                insuranceId,
                payerId,
                groupNumber,
                ...((source === 'patientProfilePage' || source === 'miniPatientProfile') && { tebraPatientId: tebraId.trim() }),
              });
          }}
          onClickValidation={validate}
          className={button}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

PatientProfile.propTypes = {
  patient: PropTypes.object,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  backButtonText: PropTypes.string,
};

const styles = () => ({
  input: {
    margin: '0.5rem 0',
    display: 'block',
  },
  button: {
    marginRight: '1em',
    marginTop: '10px'
  },
  warning: {
    color: '#e25141',
    fontSize: '12px',
  },
});

export default withStyles(styles)(PatientProfile);
